import * as React from "react";
import { createContext, Dispatch, FC, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { IFeatureTimeStamp, useAppContext } from "./AppContext";
import { useMapContext } from "./MapContext";
import { IFeatureGroup } from "@geo/js";
import { Point } from "ol/geom";
import { getCenter } from "ol/extent";
import { decodeFeatureFields } from "../Map/utils";
import { IdentifyResultsArray } from "../Map/Identify/identifyUtils";

export interface IGroup {
    value: string;
    label: string;
}

export interface ISearchContext {
    searchParam: string;
    setSearchParam: Dispatch<SetStateAction<string>>;
    loading: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
    error: string;
    setError: Dispatch<SetStateAction<string>>;
    notification: string;
    setNotification: Dispatch<SetStateAction<string>>;
    searchHelp: boolean;
    setSearchHelp: Dispatch<SetStateAction<boolean>>;
    groups: IGroup[];
    setGroups: Dispatch<SetStateAction<IGroup[]>>;
    selectedGroup: IGroup;
    setSelectedGroup: Dispatch<SetStateAction<IGroup>>;
    resultFeatures: IFeatureGroup[];
    setResultFeatures: Dispatch<SetStateAction<IFeatureGroup[]>>;
    selected: IFeatureTimeStamp;
    setSelected: Dispatch<SetStateAction<IFeatureTimeStamp>>;
}

const SearchContext = createContext<ISearchContext>(null);

export const useSearchContext = (): ISearchContext => useContext(SearchContext);

export const SearchContextProvider: FC = (props: React.PropsWithChildren<ISearchContext>) => {
    const { setLocateFeature, setHighlightFeatures } = useAppContext();
    const { configA, setFeaturesPopup } = useMapContext();
    const defaultGroups = getGroups(configA);

    const [searchParam, setSearchParam] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [notification, setNotification] = useState<string>("");
    const [searchHelp, setSearchHelp] = useState<boolean>(true);
    const [groups, setGroups] = useState<IGroup[]>(defaultGroups);
    const [selectedGroup, setSelectedGroup] = useState<IGroup>(defaultGroups.length && defaultGroups[0]);
    const [resultFeatures, setResultFeatures] = useState<IFeatureGroup[]>([]);
    const [selected, setSelected] = useState<IFeatureTimeStamp>();

    useEffect(() => {
        if (!selected || !selected.feature) {
            return;
        }

        const coordinates =
            selected.feature.getGeometry().getType() === "Point"
                ? (selected.feature.getGeometry() as Point).getCoordinates()
                : getCenter(selected.feature.getGeometry().getExtent());

        const layerInfo = selected.feature.get("layerInfo");
        const feature = decodeFeatureFields(selected.feature, layerInfo.fields);

        const results: IdentifyResultsArray = [
            [
                layerInfo.name,
                [
                    {
                        mapLayerId: selected.feature.get("serviceId"),
                        layerId: layerInfo.id,
                        layerName: layerInfo.name,
                        feature
                    }
                ]
            ]
        ];

        setLocateFeature(selected.feature);
        setFeaturesPopup({ coordinates, results });
    }, [selected]);

    const prepareSelected = useCallback((feature) => {
        setSelected({ feature, timestamp: Date.now() });
    }, []);

    const values = {
        searchParam,
        setSearchParam,
        loading,
        setLoading,
        error,
        setError,
        notification,
        setNotification,
        searchHelp,
        setSearchHelp,
        groups,
        setGroups,
        selectedGroup,
        setSelectedGroup,
        resultFeatures,
        setResultFeatures,
        selected,
        setSelected: prepareSelected
    };

    return <SearchContext.Provider value={values}>{props.children}</SearchContext.Provider>;
};

const getGroups = (config) => {
    const search = config.search;
    const groups: IGroup[] = [];
    search && search.groups && search.groups.forEach((group) => groups.push({ value: group.id, label: group.label }));

    return groups;
};
